
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js/auto';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../framework/state';
import { getDatabaseOptions, getOptionLabel, OptionValue } from '../../../../framework/service/options';
import { CostReport } from '../../../../../common/application/model/report/CostReport';
import { CustomerReport } from '../../../../../common/application/model/report/CustomerReport';

import {
    barChartOptions,
    chartBackgroundColors,
    expenseTypeColors,
    getMonthLabels,
} from '../../../service/report_service';
import { formatCurrency, sortNumerically } from '../../../service/helper_service';
import { formatMonthStrings } from '../../../../../common/framework/util/time';

@Component
export default class MonthlyBudgetChart extends Vue {
    @Prop(Object) readonly report!: CustomerReport;
    @Prop(Object) readonly prevYearReport!: CustomerReport;
    @Prop(Boolean) readonly showWithVat!: boolean;
    @Prop(Boolean) readonly showTitle!: boolean;
    @Prop(Number) readonly budgetYear!: number;
    @Prop(Boolean) readonly showBudget!: boolean;
    @Prop(Boolean) readonly showOutcome!: boolean;
    @Prop(Boolean) readonly showPreviousBudget!: boolean;
    @Prop(Boolean) readonly showPreviousOutcome!: boolean;
    @Prop(Array) readonly customerIds!: string[];
    @Prop(Boolean) readonly loading!: boolean;

    shared = sharedState;

    local = {
        monthlyBudgetChart: undefined as Chart | undefined,
        reportInvoiceRowTypes: [] as string[],
        selectedInvoiceRowTypes: [] as string[],
        monthlyCostData: MonthlyBudgetChart.resetMonthlyCostData(),
        monthlyPaidData: MonthlyBudgetChart.resetMonthlyPaidData(),
        prevYearMonthlyCostData: MonthlyBudgetChart.resetMonthlyCostData(),
        prevYearMonthlyPaidData: MonthlyBudgetChart.resetMonthlyPaidData(),
        invoiceRowTypeOptions: [] as OptionValue[],
        monthlyTotals: [] as number[],
    };

    async mounted() {
        this.local.invoiceRowTypeOptions = await getDatabaseOptions('InvoiceRowType', this);

        this.setInvoiceRowTypes();

        setTimeout(() => {
            this.addMonthlyBudgetChart();
        }, 200);
    }

    @Watch('report')
    reportChanged() {
        this.setInvoiceRowTypes();
    }

    @Watch('prevYearReport')
    prevYearReportChanged() {
        this.setInvoiceRowTypes();
    }

    @Watch('showBudget')
    showBudgetChange() {
        this.updateChart();
    }

    @Watch('showOutcome')
    showOutcomeChange() {
        this.updateChart();
    }

    @Watch('showPreviousBudget')
    showPreviousBudgetChange() {
        this.updateChart();
    }

    @Watch('showPreviousOutcome')
    showPreviousOutcomeChange() {
        this.updateChart();
    }

    @Watch('showWithVat')
    showWithVatChange() {
        this.updateChart();
    }

    @Watch('local.selectedInvoiceRowTypes')
    selectedInvoiceRowTypesChanged() {
        this.updateChart();
    }

    setColumn(invoiceRowType: string): void {
        const index = this.local.selectedInvoiceRowTypes.findIndex(
            (existingRowType) => existingRowType === invoiceRowType,
        );

        if (index === 0 && this.local.selectedInvoiceRowTypes.length === 1) {
            return;
        } else if (index > -1) {
            this.local.selectedInvoiceRowTypes.splice(index, 1);
        } else {
            this.local.selectedInvoiceRowTypes.push(invoiceRowType);
        }
    }

    private updateChart(): void {
        this.local.monthlyCostData = MonthlyBudgetChart.resetMonthlyCostData();
        this.local.monthlyPaidData = MonthlyBudgetChart.resetMonthlyPaidData();

        this.local.prevYearMonthlyCostData = MonthlyBudgetChart.resetMonthlyCostData();
        this.local.prevYearMonthlyPaidData = MonthlyBudgetChart.resetMonthlyPaidData();

        if (
            this.local.monthlyBudgetChart &&
            this.local.monthlyBudgetChart.data &&
            this.local.monthlyBudgetChart.data.datasets
        ) {
            this.local.monthlyBudgetChart.data.datasets = this.chartDataSets;
            this.local.monthlyBudgetChart.update();
        }
    }

    private async addMonthlyBudgetChart() {
        if (!this.report && !this.prevYearReport) {
            return;
        }

        const canvasContext = (document.getElementById('monthly-budget-chart') as HTMLCanvasElement)!!.getContext(
            '2d',
        )!!;

        const chartConfiguration: ChartConfiguration = {
            type: 'bar',
            data: {
                labels: getMonthLabels(this),
                datasets: this.chartDataSets,
            },
            options: {
                ...barChartOptions,
                layout: {
                    autoPadding: true,
                },
                plugins: {
                    ...barChartOptions.plugins,
                    legend: {
                        ...(barChartOptions.plugins ? barChartOptions.plugins.legend : {}),
                        display: true,
                        labels: {
                            ...(barChartOptions.plugins?.legend ? barChartOptions.plugins.legend.labels : {}),
                            filter: (legendItem, chartData) =>
                                legendItem.text === `${this.$t('label.prevYearMonthlyBudget')}` ||
                                legendItem.text === `${this.$t('label.prevYearMonthlyPaid')}`,
                        },
                        onClick: (e, legendItem, legend) => {
                            const chart = legend.chart;
                            const index = legendItem.datasetIndex;
                            if (!index) {
                                return;
                            }
                            const meta = chart.getDatasetMeta(index);

                            // Toggle the visibility
                            meta.hidden = !meta.hidden;

                            // Emit an event for the parent component
                            if (legendItem.text === this.$t('label.prevYearMonthlyBudget')) {
                                this.$emit('togglePreviousBudget');
                            } else if (legendItem.text === this.$t('label.prevYearMonthlyPaid')) {
                                this.$emit('togglePreviousOutcome');
                            }
                        }
                    },
                    title: {
                        display: true,
                        text: '',
                    },
                    tooltip: {
                        callbacks: {
                            label: (context: any) => {
                                let label = context.dataset.label || '';

                                if (label) {
                                    label += ': ';
                                }

                                if (context.parsed.y !== null && context.parsed.y !== undefined) {
                                    label += formatCurrency(context.parsed.y);
                                }

                                const labelValue = [label];

                                if (this.isCurrentYear && new Date().getMonth() < context.dataIndex) {
                                    return labelValue;
                                }

                                if (context.dataset.stack === 'PaidStack') {
                                    const monthlyTotalPaid = this.local.monthlyCostData[context.dataIndex].reduce(
                                        (previousValue, rowType) =>
                                            previousValue +
                                            (this.showWithVat ? rowType.paidIncludingVat : rowType.paidExcludingVat),
                                        0,
                                    );

                                    labelValue.push(
                                        `${this.$t('label.monthlyPaid')}: ${formatCurrency(monthlyTotalPaid)}`,
                                    );
                                    return labelValue;
                                }

                                if (context.dataset.stack === 'BudgetStack') {
                                    const monthlyTotalInvoiced = this.local.monthlyCostData[context.dataIndex].reduce(
                                        (previousValue, rowType) =>
                                            previousValue +
                                            (this.showWithVat
                                                ? rowType.invoicedIncludingVat
                                                : rowType.invoicedExcludingVat),
                                        0,
                                    );
                                    labelValue.push(
                                        `${this.$t('label.monthlyBudget')}: ${formatCurrency(monthlyTotalInvoiced)}`,
                                    );
                                    return labelValue;
                                }

                                return labelValue;
                            },
                            title: (context: any) => {
                                if (this.isCurrentYear && new Date().getMonth() < context[0].dataIndex) {
                                    return [`${context[0].label} - ${String(this.$t('field.projected'))}`];
                                }

                                return [context[0].label];
                            },
                        },
                    },
                    datalabels: {
                        anchor: 'end',
                        align: 'top',
                        formatter: (value: number, context: Context) => {
                            const totalBudgetedDataset: any[] = [];
                            const totalPaidDataset: any[] = [];
                            if (!context.chart.data.datasets) {
                                return;
                            }

                            context.chart.data.datasets.forEach((dataset) => {
                                if (dataset.stack === 'BudgetStack') {
                                    if (dataset.data[context.dataIndex] !== undefined) {
                                        totalBudgetedDataset.push(dataset.data[context.dataIndex]);
                                    }
                                } else if (dataset.stack === 'PaidStack') {
                                    if (dataset.data[context.dataIndex] !== undefined) {
                                        totalPaidDataset.push(dataset.data[context.dataIndex]);
                                    }
                                }
                            });

                            const sumBudgeted = totalBudgetedDataset.reduce((total, datapoint) => total + datapoint, 0);
                            const sumPaid = totalPaidDataset.reduce((total, datapoint) => total + datapoint, 0);

                            const budgetDatasetLastElement = context.chart.data.datasets
                                .filter((dataset) => dataset.stack === 'BudgetStack')
                                .sort(sortNumerically('order'))
                                .pop()!!;
                            const paidDatasetLastElement = context.chart.data.datasets
                                .filter((dataset) => dataset.stack === 'PaidStack')
                                .sort(sortNumerically('order'))
                                .pop()!!;

                            if (
                                context.dataset.stack === 'BudgetStack' &&
                                budgetDatasetLastElement.label === context.dataset.label
                            ) {
                                return `${formatCurrency(sumBudgeted)}`;
                            } else if (
                                context.dataset.stack === 'PaidStack' &&
                                paidDatasetLastElement.label === context.dataset.label
                            ) {
                                return `${formatCurrency(sumPaid)}`;
                            } else {
                                return '';
                            }
                        },
                        color: '#344B9F',
                    },
                },
                scales: {
                    x: {
                        grid: {
                            color: '#344B9F',
                        },
                        title: {
                            color: '#344B9F',
                        },
                    },
                    y: {
                        ticks: {
                            callback: (value) => formatCurrency(value as number),
                        },
                        grid: {
                            color: '#344B9F',
                        },
                        title: {
                            color: '#344B9F',
                        },
                        type: 'linear',
                        display: true,
                        position: 'left',
                    },
                },
            },
            plugins: [ChartDataLabels],
        };

        this.local.monthlyBudgetChart = new Chart(canvasContext, chartConfiguration);
    }

    private static get backgroundColors(): { [rowType: string]: string } {
        return expenseTypeColors;
    }

    private static get lineBackgroundColors(): string[] {
        return chartBackgroundColors;
    }

    private get chartDataSets(): ChartDataset[] {
        const beginDate = DateTime.utc(this.budgetYear).startOf('year').toJSDate();
        const months = formatMonthStrings(beginDate.getFullYear(), beginDate.getMonth(), 11);

        months.forEach((month, monthIndex) => {
            this.local.selectedInvoiceRowTypes.forEach((invoiceRowType) => {
                this.local.monthlyCostData[monthIndex].push({
                    month,
                    invoiceRowType: invoiceRowType.toLowerCase(),
                    invoiceRowTypeLabel: getOptionLabel(
                        invoiceRowType.toString().toLowerCase(),
                        this.local.invoiceRowTypeOptions,
                    ),
                    invoicedExcludingVat: 0,
                    invoicedVat: 0,
                    invoicedIncludingVat: 0,
                    paidExcludingVat: 0,
                    paidVat: 0,
                    paidIncludingVat: 0,
                    projectedExcludingVat: 0,
                    projectedVat: 0,
                    projectedIncludingVat: 0,
                });
            });
        });

        for (const row of this.report.costRows) {
            const monthIndex = months.findIndex((month) => month === row.month);

            if (monthIndex === -1) {
                console.error('Months are not ok', row.month);
                continue;
            }

            const rowTypeIndex = this.local.monthlyCostData[monthIndex].findIndex(
                (rowType: any) => rowType.invoiceRowType === row.invoiceRowType,
            );

            if (rowTypeIndex === -1) {
                continue;
            }

            this.local.monthlyCostData[monthIndex][rowTypeIndex].invoicedExcludingVat += row.invoiced;
            this.local.monthlyCostData[monthIndex][rowTypeIndex].invoicedVat += row.invoicedVat;
            this.local.monthlyCostData[monthIndex][rowTypeIndex].invoicedIncludingVat += row.invoiced + row.invoicedVat;
            this.local.monthlyCostData[monthIndex][rowTypeIndex].paidExcludingVat += row.paid;
            this.local.monthlyCostData[monthIndex][rowTypeIndex].paidVat += row.paidVat;
            this.local.monthlyCostData[monthIndex][rowTypeIndex].paidIncludingVat += row.paid + row.paidVat;
            this.local.monthlyCostData[monthIndex][rowTypeIndex].projectedExcludingVat += row.projected;
            this.local.monthlyCostData[monthIndex][rowTypeIndex].projectedVat += row.projectedVat;
            this.local.monthlyCostData[monthIndex][rowTypeIndex].projectedIncludingVat +=
                row.projected + row.projectedVat;

            if (this.showWithVat) {
                this.local.monthlyPaidData[monthIndex] += parseFloat(((row.paid || 0) + (row.paidVat || 0)).toFixed(2));
            } else {
                this.local.monthlyPaidData[monthIndex] += parseFloat((row.paid || 0).toFixed(2));
            }
        }

        const datasets: ChartDataset[] = [];
        const cumulativeData: number[] = [];

        this.local.selectedInvoiceRowTypes.forEach((invoiceRowType, i) => {
            const invoicedRowTypeData: number[] = [];
            const paidRowTypeData: number[] = [];

            for (const monthData of this.local.monthlyCostData) {
                const rowTypeIndex = monthData.findIndex((monthRow: any) => monthRow.invoiceRowType === invoiceRowType);

                if (this.showWithVat) {
                    invoicedRowTypeData.push(monthData[rowTypeIndex].invoicedIncludingVat);
                    paidRowTypeData.push(monthData[rowTypeIndex].paidIncludingVat);
                } else {
                    invoicedRowTypeData.push(monthData[rowTypeIndex].invoicedExcludingVat);
                    paidRowTypeData.push(monthData[rowTypeIndex].paidExcludingVat);
                }
            }

            invoicedRowTypeData.forEach((invoicedValue, index) => {
                if (this.isCurrentYear && invoicedValue === 0 && index > 0 && invoicedRowTypeData[index - 1] > 0) {
                    invoicedRowTypeData[index] = invoicedRowTypeData[index - 1];
                }
            });

            datasets.push({
                label: `${getOptionLabel(invoiceRowType.toString().toLowerCase(), this.local.invoiceRowTypeOptions)}`,
                data: invoicedRowTypeData,
                backgroundColor: `${MonthlyBudgetChart.backgroundColors[invoiceRowType]}d9`,
                borderColor: MonthlyBudgetChart.backgroundColors[invoiceRowType],
                borderWidth: 2,
                order: this.local.invoiceRowTypeOptions.findIndex(
                    (invoiceRowTypeOption) => invoiceRowTypeOption.id === invoiceRowType,
                ),
                type: 'bar',
                stack: 'BudgetStack',
                hidden: !this.showBudget,
            });

            datasets.push({
                label: `${getOptionLabel(invoiceRowType.toString().toLowerCase(), this.local.invoiceRowTypeOptions)}`,
                data: paidRowTypeData,
                backgroundColor: MonthlyBudgetChart.backgroundColors[invoiceRowType],
                order:
                    this.local.invoiceRowTypeOptions.findIndex(
                        (invoiceRowTypeOption) => invoiceRowTypeOption.id === invoiceRowType,
                    ) * 2,
                type: 'bar',
                stack: 'PaidStack',
                hidden: !this.showOutcome,
            });
        });

        const monthlyTotals: number[] = [];

        this.local.monthlyCostData.forEach((monthlyCost, index) => {
            const monthlyTotal = monthlyCost.reduce((previousValue, costRow) => {
                if (this.showWithVat) {
                    return previousValue + costRow.paidIncludingVat;
                } else {
                    return previousValue + costRow.paidExcludingVat;
                }
            }, 0);

            if (index === 0) {
                monthlyTotals.push(monthlyTotal);
            } else {
                monthlyTotals.push(monthlyTotal > 0 ? monthlyTotal : monthlyTotals[index - 1]);
            }
        });

        monthlyTotals.forEach((amount, index) => {
            if (index === 0) {
                cumulativeData.push(amount);
            } else {
                cumulativeData.push(cumulativeData[index - 1] + amount);
            }
        });

        // Line chart to show previous year data

        if (this.prevYearReport && Object.keys(this.prevYearReport).length) {
            const beginDatePrevYear = DateTime.utc(this.budgetYear - 1)
                .startOf('year')
                .toJSDate();
            const monthsPrevYear = formatMonthStrings(
                beginDatePrevYear.getFullYear(),
                beginDatePrevYear.getMonth(),
                11,
            );

            monthsPrevYear.forEach((month, monthIndex) => {
                this.local.selectedInvoiceRowTypes.forEach((invoiceRowType) => {
                    this.local.prevYearMonthlyCostData[monthIndex].push({
                        month,
                        invoiceRowType: invoiceRowType.toLowerCase(),
                        invoiceRowTypeLabel: getOptionLabel(
                            invoiceRowType.toString().toLowerCase(),
                            this.local.invoiceRowTypeOptions,
                        ),
                        invoicedExcludingVat: 0,
                        invoicedVat: 0,
                        invoicedIncludingVat: 0,
                        paidExcludingVat: 0,
                        paidVat: 0,
                        paidIncludingVat: 0,
                        projectedExcludingVat: 0,
                        projectedVat: 0,
                        projectedIncludingVat: 0,
                    });
                });
            });

            for (const row of this.prevYearReport.costRows) {
                const monthIndex = monthsPrevYear.findIndex((month) => month === row.month);

                if (monthIndex === -1) {
                    continue;
                }

                const rowTypeIndex = this.local.prevYearMonthlyCostData[monthIndex].findIndex(
                    (rowType: any) => rowType.invoiceRowType === row.invoiceRowType,
                );

                if (rowTypeIndex === -1) {
                    continue;
                }

                this.local.prevYearMonthlyCostData[monthIndex][rowTypeIndex].invoicedExcludingVat += row.invoiced;
                this.local.prevYearMonthlyCostData[monthIndex][rowTypeIndex].invoicedVat += row.invoicedVat;
                this.local.prevYearMonthlyCostData[monthIndex][rowTypeIndex].invoicedIncludingVat +=
                    row.invoiced + row.invoicedVat;
                this.local.prevYearMonthlyCostData[monthIndex][rowTypeIndex].paidExcludingVat += row.paid;
                this.local.prevYearMonthlyCostData[monthIndex][rowTypeIndex].paidVat += row.paidVat;
                this.local.prevYearMonthlyCostData[monthIndex][rowTypeIndex].paidIncludingVat += row.paid + row.paidVat;
                this.local.prevYearMonthlyCostData[monthIndex][rowTypeIndex].projectedExcludingVat += row.projected;
                this.local.prevYearMonthlyCostData[monthIndex][rowTypeIndex].projectedVat += row.projectedVat;
                this.local.prevYearMonthlyCostData[monthIndex][rowTypeIndex].projectedIncludingVat +=
                    row.projected + row.projectedVat;

                if (this.showWithVat) {
                    this.local.prevYearMonthlyPaidData[monthIndex] += parseFloat(
                        ((row.paid || 0) + (row.paidVat || 0)).toFixed(2),
                    );
                } else {
                    this.local.prevYearMonthlyPaidData[monthIndex] += parseFloat((row.paid || 0).toFixed(2));
                }
            }

            let totalBudgetedRowTypeData: number[] = [];
            let totalPaidRowTypeData: number[] = [];

            this.local.selectedInvoiceRowTypes.forEach((invoiceRowType, i) => {
                const budgetedRowTypeData: number[] = [];
                const paidRowTypeData: number[] = [];

                for (const monthData of this.local.prevYearMonthlyCostData) {
                    const rowTypeIndex = monthData.findIndex(
                        (monthRow: any) => monthRow.invoiceRowType === invoiceRowType,
                    );

                    if (this.showWithVat) {
                        budgetedRowTypeData.push(monthData[rowTypeIndex].invoicedIncludingVat);
                        paidRowTypeData.push(monthData[rowTypeIndex].paidIncludingVat);
                    } else {
                        budgetedRowTypeData.push(monthData[rowTypeIndex].invoicedExcludingVat);
                        paidRowTypeData.push(monthData[rowTypeIndex].paidExcludingVat);
                    }
                }

                budgetedRowTypeData.forEach((budgetedValue, index) => {
                    if (budgetedValue === 0 && index > 0 && budgetedRowTypeData[index - 1] > 0) {
                        budgetedRowTypeData[index] = budgetedRowTypeData[index - 1];
                    }

                    totalBudgetedRowTypeData[index] = (totalBudgetedRowTypeData[index] || 0) + budgetedValue;
                });

                paidRowTypeData.forEach((paidValue, index) => {
                    if (paidValue === 0 && index > 0 && paidRowTypeData[index - 1] > 0) {
                        paidRowTypeData[index] = paidRowTypeData[index - 1];
                    }

                    totalPaidRowTypeData[index] = (totalPaidRowTypeData[index] || 0) + paidValue;
                });
            });

            datasets.push({
                label: `${this.$t('label.prevYearMonthlyBudget')}`,
                data: totalBudgetedRowTypeData,
                borderWidth: 2,
                hidden: !this.showPreviousBudget,
                type: 'line',
                borderColor: MonthlyBudgetChart.lineBackgroundColors[1],
                backgroundColor: MonthlyBudgetChart.lineBackgroundColors[1],
                fill: false,
            });

            datasets.push({
                label: `${this.$t('label.prevYearMonthlyPaid')}`,
                data: totalPaidRowTypeData,
                borderWidth: 2,
                hidden: !this.showPreviousOutcome,
                type: 'line',
                borderColor: MonthlyBudgetChart.lineBackgroundColors[2],
                backgroundColor: MonthlyBudgetChart.lineBackgroundColors[2],
                fill: false,
            });
        }

        return datasets;
    }

    private static resetMonthlyCostData(): CostReport[][] {
        return [[], [], [], [], [], [], [], [], [], [], [], []] as CostReport[][];
    }

    private static resetMonthlyPaidData(): number[] {
        return [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    }

    private sortReportRowTypes = (a: string, b: string) => {
        const rowTypeIndexA = this.local.invoiceRowTypeOptions.findIndex((option) => option.id === a);
        const rowTypeIndexB = this.local.invoiceRowTypeOptions.findIndex((option) => option.id === b);

        if (rowTypeIndexA < rowTypeIndexB) {
            return -1;
        }

        if (rowTypeIndexA > rowTypeIndexB) {
            return 1;
        }

        return 0;
    };

    private setInvoiceRowTypes(): void {
        this.local.reportInvoiceRowTypes = Array.from(
            new Set(this.report.costRows.map((r) => r.invoiceRowType.toString())),
        ).sort(this.sortReportRowTypes);
        this.local.selectedInvoiceRowTypes = [...this.local.reportInvoiceRowTypes];
    }

    private get isCurrentYear(): boolean {
        return this.budgetYear === new Date().getFullYear();
    }
}
